import { ROUTES } from 'Utils/constants'

const CUSTOMER_DISPLAY_WINDOW_NAME = 'customer_display'
const CONFIG_STORAGE_KEYS = {
  left: 'customerDisplayLeft',
  top: 'customerDisplayTop',
  width: 'customerDisplayWidth',
  height: 'customerDisplayHeight',
  screenSignature: 'customerDisplayScreenSignature',
  updatedAt: 'customerDisplayConfigUpdatedAt',
}
const STORED_CONFIG_TTL_MS = 7 * 24 * 60 * 60 * 1000

const getCustomerDisplayWindowName = () => CUSTOMER_DISPLAY_WINDOW_NAME

export const getCustomerDisplayUrl = () =>
  `${globalThis.location.origin}${ROUTES.customerDisplay}`

const parseStoredNumber = key => {
  const value = Number(globalThis.localStorage?.getItem(key))

  return Number.isFinite(value) ? value : null
}

const getCurrentScreenSignature = () =>
  JSON.stringify({
    width: globalThis.screen.width,
    height: globalThis.screen.height,
    availWidth: globalThis.screen.availWidth,
    availHeight: globalThis.screen.availHeight,
    availLeft: globalThis.screen.availLeft ?? 0,
    availTop: globalThis.screen.availTop ?? 0,
    devicePixelRatio: globalThis.devicePixelRatio || 1,
  })

const isStoredConfigFresh = () => {
  const updatedAt = parseStoredNumber(CONFIG_STORAGE_KEYS.updatedAt)
  if (!updatedAt) return false

  return Date.now() - updatedAt <= STORED_CONFIG_TTL_MS
}

const getStoredConfig = () => {
  const left = parseStoredNumber(CONFIG_STORAGE_KEYS.left)
  const top = parseStoredNumber(CONFIG_STORAGE_KEYS.top)
  const width = parseStoredNumber(CONFIG_STORAGE_KEYS.width)
  const height = parseStoredNumber(CONFIG_STORAGE_KEYS.height)
  const screenSignature = globalThis.localStorage?.getItem(
    CONFIG_STORAGE_KEYS.screenSignature
  )

  if ([left, top, width, height].some(value => value === null)) return null
  if (width <= 0 || height <= 0) return null
  if (screenSignature !== getCurrentScreenSignature()) return null
  if (!isStoredConfigFresh()) return null

  return { width, height, left, top }
}

// Last-resort fallback for browsers without Window Management API access.
const getLegacyConfig = () => ({
  width: globalThis.screen.availWidth || globalThis.screen.width,
  height: globalThis.screen.availHeight || globalThis.screen.height,
  left: globalThis.screen.width,
  top: globalThis.screen.availTop || 0,
})

// Uses the Window Management API to find the actual secondary screen position.
// Works for any monitor layout (left, right, above, below).
// Returns null if the API is unavailable or permission is denied.
const getSecondaryScreenConfig = async () => {
  if (!globalThis.screen.isExtended) return null
  if (typeof globalThis.getScreenDetails !== 'function') return null

  try {
    const screenDetails = await globalThis.getScreenDetails()
    const secondary = screenDetails.screens.find(
      s => s !== screenDetails.currentScreen
    )
    if (!secondary) return null

    return {
      width: secondary.availWidth ?? secondary.width,
      height: secondary.availHeight ?? secondary.height,
      left: secondary.availLeft ?? secondary.left,
      top: secondary.availTop ?? secondary.top,
    }
  } catch (error) {
    console.warn('No se pudo obtener la pantalla secundaria:', error)
    return null
  }
}

const hasKnownSecondaryScreen = screenDetails => {
  if (globalThis.screen?.isExtended === false) return false
  if (screenDetails?.screens?.length > 1) return true

  return globalThis.screen?.isExtended === true
}

const getInitialConfig = () => getStoredConfig() || getLegacyConfig()

const buildWindowFeatures = ({ width, height, left, top }) =>
  [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'popup=yes',
    'toolbar=no',
    'menubar=no',
    'location=no',
    'status=no',
    'scrollbars=no',
    'resizable=yes',
  ].join(',')

const persistConfig = ({ width, height, left, top }) => {
  globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.left, left)
  globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.top, top)
  globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.width, width)
  globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.height, height)
  globalThis.localStorage?.setItem(
    CONFIG_STORAGE_KEYS.screenSignature,
    getCurrentScreenSignature()
  )
  globalThis.localStorage?.setItem(CONFIG_STORAGE_KEYS.updatedAt, Date.now())
}

const applyConfig = (win, config, { shouldPersist = true } = {}) => {
  try {
    win.moveTo?.(config.left, config.top)
    win.resizeTo?.(config.width, config.height)
  } catch (error) {
    console.warn('No se pudo posicionar la ventana de Customer Display:', error)
    return
  }

  if (shouldPersist) {
    persistConfig(config)
  }
}

// Repositions the window on the actual secondary screen once the async API responds.
// If the API is unavailable, the window stays at the stored or legacy position.
const repositionOnSecondaryScreen = win => {
  getSecondaryScreenConfig().then(config => {
    if (config && !win.closed) applyConfig(win, config)
  })
}

export const positionCurrentCustomerDisplayWindow = async () => {
  const config = (await getSecondaryScreenConfig()) || getInitialConfig()

  if (!config) return false

  applyConfig(globalThis, config)
  return true
}

export const watchSecondaryScreenDisconnect = async onDisconnect => {
  let isDisposed = false
  let hadSecondaryScreen = false
  let didNotifyDisconnect = false
  let screenDetails = null

  const checkScreens = () => {
    if (isDisposed) return

    const hasSecondaryScreen = hasKnownSecondaryScreen(screenDetails)

    if (hasSecondaryScreen) {
      hadSecondaryScreen = true
      return
    }

    if (hadSecondaryScreen && !didNotifyDisconnect) {
      didNotifyDisconnect = true
      onDisconnect()
    }
  }

  const intervalId = globalThis.setInterval(checkScreens, 2000)

  if (typeof globalThis.getScreenDetails === 'function') {
    try {
      screenDetails = await globalThis.getScreenDetails()
      screenDetails.addEventListener?.('screenschange', checkScreens)
    } catch (error) {
      console.warn(
        'No se pudo observar cambios de pantallas para Customer Display:',
        error
      )
      screenDetails = null
    }
  }

  globalThis.addEventListener?.('focus', checkScreens)
  globalThis.addEventListener?.('resize', checkScreens)
  checkScreens()

  return () => {
    isDisposed = true
    globalThis.clearInterval(intervalId)
    screenDetails?.removeEventListener?.('screenschange', checkScreens)
    globalThis.removeEventListener?.('focus', checkScreens)
    globalThis.removeEventListener?.('resize', checkScreens)
  }
}

export const openCustomerDisplayWindow = ({ url } = {}) => {
  // window.open must be called synchronously to preserve the browser's transient
  // activation (user gesture). We open with the best known position and
  // reposition asynchronously once getScreenDetails() responds.
  const config = getInitialConfig()
  let win = null

  try {
    win = globalThis.open(
      url || getCustomerDisplayUrl(),
      getCustomerDisplayWindowName(),
      buildWindowFeatures(config)
    )
  } catch (error) {
    console.warn('No se pudo abrir la ventana de Customer Display:', error)
    return null
  }

  if (!win) return null

  applyConfig(win, config, { shouldPersist: false })
  repositionOnSecondaryScreen(win)

  return win
}

export const navigateCustomerDisplayWindow = customerDisplayWindow => {
  if (!customerDisplayWindow || customerDisplayWindow.closed) {
    return Boolean(openCustomerDisplayWindow())
  }

  const config = getInitialConfig()
  applyConfig(customerDisplayWindow, config, { shouldPersist: false })
  repositionOnSecondaryScreen(customerDisplayWindow)
  customerDisplayWindow.location.href = getCustomerDisplayUrl()

  return true
}
